import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ECOSYSTEM_PARTICIPANTS } from "@/lib/data";

const CHIP_FILL = "#FFFFFF";
const CHIP_STROKE = "rgba(11,21,38,0.16)";
const LABEL = "#4C5F7A";
const ELECTRIC = "#0B63F3";
const TEAL = "#00856B";
const INK_TEXT = "#0A1628";

function Chip({ x, y, w, label, accent = false }) {
  return (
    <g>
      <rect x={x} y={y} width={w} height={34} rx={10} fill={CHIP_FILL} stroke={accent ? "rgba(0,133,107,0.55)" : CHIP_STROKE} strokeWidth={1} />
      <text x={x + w / 2} y={y + 21} textAnchor="middle" fill={accent ? TEAL : INK_TEXT} fontSize={11} fontFamily="JetBrains Mono, monospace" letterSpacing={1}>
        {label}
      </text>
    </g>
  );
}

function Path({ d, color = ELECTRIC, slow = false }) {
  return (
    <path d={d} fill="none" stroke={color} strokeWidth={1.4} strokeOpacity={0.55} className={slow ? "flow-dash-slow" : "flow-dash"} />
  );
}

export function NetworkHero() {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 60, damping: 18 });
  const sy = useSpring(my, { stiffness: 60, damping: 18 });
  const tx = useTransform(sx, [-0.5, 0.5], [-12, 12]);
  const ty = useTransform(sy, [-0.5, 0.5], [-8, 8]);

  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <div ref={ref} onMouseMove={onMove} className="relative" data-testid="hero-network-visual" aria-hidden="true">
      <div className="pointer-events-none absolute inset-0 bg-glow-blue" />
      <motion.div style={{ x: tx, y: ty }}>
        <svg viewBox="0 0 640 560" className="w-full" role="img" aria-label="Animated financial infrastructure network">
          <Path d="M 120 280 C 160 280, 160 280, 196 280" />
          <Path d="M 268 260 C 310 220, 320 190, 352 172" />
          <Path d="M 272 280 C 310 280, 316 280, 352 280" />
          <Path d="M 268 300 C 310 340, 320 368, 352 388" />
          <Path d="M 452 172 C 500 190, 510 240, 522 262" color={TEAL} slow />
          <Path d="M 452 280 C 490 280, 496 274, 522 272" color={TEAL} slow />
          <Path d="M 452 388 C 500 366, 510 306, 522 294" color={TEAL} slow />
          <Path d="M 232 326 C 232 400, 300 430, 380 430 S 510 380, 545 320" color={TEAL} slow />

          <Chip x={30} y={263} w={92} label="CONSUMERS" />
          <Chip x={352} y={155} w={100} label="PAYMENTS" />
          <Chip x={352} y={263} w={100} label="BANKING" />
          <Chip x={352} y={371} w={100} label="INSURANCE" />
          <Chip x={522} y={246} w={100} label="BANKS" />
          <Chip x={522} y={296} w={100} label="NBFCs" accent />

          <circle cx={232} cy={280} r={38} fill="#FFFFFF" stroke={ELECTRIC} strokeOpacity={0.6} strokeWidth={1.4} />
          <circle cx={232} cy={280} r={38} fill="none" stroke={ELECTRIC} strokeOpacity={0.5} className="pulse-ring" />
          <image href="/assets/timepe-mark.png" x={232 - 24} y={280 - 24} width={48} height={48} />
          <text x={232} y={240} textAnchor="middle" fill={LABEL} fontSize={9} fontFamily="JetBrains Mono, monospace" letterSpacing={2}>
            PLATFORM
          </text>
          <text x={572} y={230} textAnchor="middle" fill={LABEL} fontSize={9} fontFamily="JetBrains Mono, monospace" letterSpacing={2}>
            ECOSYSTEM
          </text>
          <text x={402} y={140} textAnchor="middle" fill={LABEL} fontSize={9} fontFamily="JetBrains Mono, monospace" letterSpacing={2}>
            SERVICE RAILS
          </text>

          <g>
            <rect x={300} y={452} width={180} height={40} rx={12} fill="#FFFFFF" stroke="rgba(0,133,107,0.5)" />
            <text x={390} y={476} textAnchor="middle" fill={TEAL} fontSize={11} fontFamily="JetBrains Mono, monospace" letterSpacing={1.5}>
              TIMEPE INTELLIGENCE
            </text>
          </g>
          <Path d="M 232 318 C 240 400, 280 430, 310 448" color={TEAL} />
          <Path d="M 480 462 C 540 440, 570 380, 572 336" color={TEAL} />
        </svg>
      </motion.div>
    </div>
  );
}

export function EcosystemNet() {
  const cx = 300;
  const cy = 300;
  const r = 200;
  const nodes = ECOSYSTEM_PARTICIPANTS.map((label, i) => {
    const angle = (i / ECOSYSTEM_PARTICIPANTS.length) * Math.PI * 2 - Math.PI / 2;
    return { label, x: cx + r * Math.cos(angle), y: cy + r * Math.sin(angle) };
  });
  return (
    <svg viewBox="0 0 600 600" className="mx-auto w-full max-w-[560px]" role="img" aria-label="TimePe ecosystem network connecting banks, NBFCs, FinTechs, PSPs, insurers, agents, retailers, merchants, citizens and institutions" data-testid="ecosystem-network-visual">
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="rgba(11,21,38,0.12)" strokeDasharray="2 6" />
      <circle cx={cx} cy={cy} r={r * 0.62} fill="none" stroke="rgba(11,21,38,0.08)" strokeDasharray="2 6" />
      {nodes.map((n, i) => (
        <path key={i} d={`M ${cx} ${cy} L ${n.x} ${n.y}`} fill="none" stroke={i % 2 ? ELECTRIC : TEAL} strokeWidth={1} strokeOpacity={0.35} className="flow-dash-slow" />
      ))}
      <circle cx={cx} cy={cy} r={46} fill="#FFFFFF" stroke={ELECTRIC} strokeOpacity={0.6} strokeWidth={1.4} />
      <circle cx={cx} cy={cy} r={46} fill="none" stroke={ELECTRIC} strokeOpacity={0.5} className="pulse-ring" />
      <image href="/assets/timepe-mark.png" x={cx - 28} y={cy - 28} width={56} height={56} />
      {nodes.map((n, i) => {
        const w = Math.max(72, n.label.length * 8 + 22);
        return (
          <g key={i}>
            <rect x={n.x - w / 2} y={n.y - 16} width={w} height={32} rx={16} fill={CHIP_FILL} stroke={CHIP_STROKE} />
            <text x={n.x} y={n.y + 4} textAnchor="middle" fill={INK_TEXT} fontSize={10.5} fontFamily="JetBrains Mono, monospace" letterSpacing={0.8}>
              {n.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
